import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { paths } from '../api/schema.js';
import { formatDate, formatNumber } from '../i18n.js';
import { flagOf, regionName } from './geo.js';
import { ShieldIcon } from './icons.js';
import { Card, LinkButton, SeverityBadge } from './ui.js';

type Overview =
  paths['/sites/{siteId}/overview']['get']['responses'][200]['content']['application/json'];

/** One fact: a label and its value; `tone` marks a value that needs attention. */
interface Fact {
  key: string;
  label: string;
  value: ReactNode;
  tone?: keyof typeof TONE | undefined;
}

const TONE = { warn: 'text-amber-800', bad: 'text-red-800', good: 'text-green-800' } as const;

/**
 * "Die Website auf einen Blick" (T-94): where the site runs, what it is built with, certificate,
 * domain, mail and the tools on the home page. Facts not known yet are left out.
 */
export function SiteFacts({
  overview: o,
  timeZone,
}: {
  overview: Overview;
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const p = o.profile;
  const h = p.hosting;
  const ssl = o.security.ssl;
  const domain = o.security.domain;
  const date = (iso: string) => formatDate(iso, lang, timeZone);
  const list = (names: string[]) => names.join(', ');
  const yes = (ok: boolean) => t(ok ? 'facts.yes' : 'facts.no');

  const facts: Fact[] = [];
  // Whether there are weaknesses, always; which ones only for the confirmed owner (T-101).
  const weak = o.weaknesses;
  facts.push({
    key: 'weaknesses',
    label: t('facts.weaknesses'),
    value: !o.health ? (
      t('facts.weaknessesPending')
    ) : weak.count === 0 || !weak.worst ? (
      t('facts.weaknessesNone')
    ) : (
      <>
        <span className="flex flex-wrap items-center gap-2">
          {t('facts.weaknessesFound', { n: weak.count })}
          <SeverityBadge severity={weak.worst} />
        </span>
        {weak.detailsHidden ? (
          <LinkButton to={`/sites/${o.site.id}/settings`} size="sm" className="mt-2">
            <ShieldIcon />
            {t('facts.weaknessesHidden')}
          </LinkButton>
        ) : (
          <LinkButton to={`/sites/${o.site.id}/findings`} size="sm" className="mt-2">
            {t('facts.weaknessesShow')}
          </LinkButton>
        )}
      </>
    ),
    tone: !o.health
      ? undefined
      : weak.count === 0
        ? 'good'
        : weak.worst === 'critical' || weak.worst === 'high'
          ? 'bad'
          : 'warn',
  });
  if (h?.country)
    facts.push({
      key: 'location',
      label: t('facts.location'),
      value: (
        <>
          <span aria-hidden="true">{flagOf(h.country)} </span>
          {regionName(h.country, lang)}
          {h.cdn ? ` · ${t('facts.viaCdn', { cdn: h.cdn })}` : null}
        </>
      ),
    });
  if (h?.org)
    facts.push({
      key: 'hoster',
      label: t('facts.hoster'),
      value: h.asn ? `${h.org} (AS${h.asn})` : h.org,
    });
  if (h)
    facts.push({
      key: 'ip',
      label: t('facts.ip'),
      value: <span className="font-mono text-sm">{h.ip}</span>,
    });
  facts.push({
    key: 'system',
    label: t('facts.system'),
    value: o.site.cmsDetected
      ? `${o.site.cmsDetected}${p.wordpressVersion ? ` ${p.wordpressVersion}` : ''}`
      : t('facts.unknownSystem'),
  });
  if (ssl)
    facts.push({
      key: 'ssl',
      label: t('facts.ssl'),
      value:
        ssl.problems.length > 0 || (ssl.daysLeft !== null && ssl.daysLeft < 0)
          ? t('facts.sslProblem')
          : ssl.validTo
            ? t('facts.until', {
                date: date(ssl.validTo),
                count: Math.max(0, ssl.daysLeft ?? 0),
              })
            : '–',
      tone:
        ssl.problems.length > 0 || (ssl.daysLeft !== null && ssl.daysLeft < 0)
          ? 'bad'
          : ssl.daysLeft !== null && ssl.daysLeft < 14
            ? 'warn'
            : undefined,
    });
  if (domain)
    facts.push({
      key: 'domain',
      label: t('facts.domain'),
      value: (
        <>
          {domain.expiresAt
            ? t('facts.domainUntil', {
                date: date(domain.expiresAt),
                count: Math.max(0, domain.daysLeft ?? 0),
              })
            : domain.noExpiryPublished
              ? t('facts.noExpiry')
              : '–'}
          {p.registrar ? (
            <span className="block text-sm font-normal text-slate-700">
              {t('facts.registrar', { name: p.registrar })}
            </span>
          ) : null}
        </>
      ),
      tone: domain.daysLeft !== null && domain.daysLeft < 30 ? 'warn' : undefined,
    });
  if (p.email)
    facts.push({
      key: 'email',
      label: t('facts.email'),
      value: (
        <>
          {t(p.email.mx ? 'facts.mailOn' : 'facts.mailOff')}
          {p.email.ownerOnly ? null : (
            <span className="block text-sm font-normal text-slate-700">
              SPF: {yes(p.email.spf === true)} · DMARC:{' '}
              {p.email.dmarc ? t(`facts.dmarc.${p.email.dmarc}`) : t('facts.no')}
            </span>
          )}
        </>
      ),
      tone:
        !p.email.ownerOnly && p.email.mx && (!p.email.spf || !p.email.dmarc) ? 'warn' : undefined,
    });
  if (o.availability.responseMs !== null)
    facts.push({
      key: 'response',
      label: t('facts.response'),
      value: `${formatNumber(o.availability.responseMs / 1000, lang, 2)} s`,
      tone: o.availability.responseMs >= 3000 ? 'warn' : undefined,
    });
  if (o.availability.uptime30d !== null)
    facts.push({
      key: 'uptime',
      label: t('facts.uptime30d'),
      value: `${formatNumber(o.availability.uptime30d, lang, 2)} %`,
      tone: o.availability.uptime30d < 99 ? 'warn' : undefined,
    });
  if (p.trackers)
    facts.push({
      key: 'trackers',
      label: t('facts.trackers'),
      value: p.trackers.length > 0 ? list(p.trackers) : t('facts.noneFound'),
    });
  if (p.cookieBanners)
    facts.push({
      key: 'cookies',
      label: t('facts.cookieBanner'),
      value: p.cookieBanners.length > 0 ? list(p.cookieBanners) : t('facts.noneFound'),
      tone: p.cookieBanners.length === 0 && (p.trackers?.length ?? 0) > 0 ? 'warn' : undefined,
    });
  facts.push({
    key: 'since',
    label: t('facts.since'),
    value: date(o.site.createdAt),
  });
  facts.push({
    key: 'owner',
    label: t('facts.owner'),
    value: o.site.verified ? (
      t('facts.verified')
    ) : (
      <>
        {t('facts.notVerified')}
        <LinkButton
          to={`/sites/${o.site.id}/settings`}
          variant="primary"
          size="sm"
          className="mt-2 flex w-fit"
        >
          <ShieldIcon />
          {t('facts.verifyCta')}
        </LinkButton>
      </>
    ),
    tone: o.site.verified ? 'good' : 'warn',
  });

  return (
    <Card className="mb-6">
      <h2 className="mb-4 text-xl font-semibold">{t('facts.title')}</h2>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
        {facts.map((f) => (
          <div key={f.key} className="min-w-0 border-l-2 border-slate-200 pl-3">
            <dt className="text-sm text-slate-700">{f.label}</dt>
            <dd className={`font-semibold break-words ${f.tone ? TONE[f.tone] : ''}`}>{f.value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
